import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateHiringGoalTargets } from '@/api-clients/update-hiring-goal-targets';
import { useToaster } from '@/components/base/toaster';
import { queryKeys } from '@/query-keys';

interface UseUpdateHiringGoalTargetsOptions {
  successMessage: string;
  errorMessage: string;
  /** Called after the success toast fires and before queries are invalidated. */
  onSuccess?: () => void;
}

/**
 * Shared mutation for persisting hiring-goal targets (applicant conversion or
 * max budget) from the analytics card popovers — both call the same
 * `PATCH /dashboard` endpoint and invalidate the same queries on success.
 */
export function useUpdateHiringGoalTargets({
  successMessage,
  errorMessage,
  onSuccess,
}: UseUpdateHiringGoalTargetsOptions) {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useToaster();

  return useMutation({
    mutationFn: updateHiringGoalTargets,
    onSuccess: async () => {
      notifySuccess({ message: successMessage });
      onSuccess?.();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.openingDetails.detail,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.openingDetails.channels.list,
        }),
      ]);
    },
    onError: () => {
      notifyError({ message: errorMessage });
    },
  });
}
